import { useEffect, useState } from "react";
import { t } from "../../lib/i18n";
import { toast } from "sonner";
import { useSiteSettings } from "../../lib/siteSettings";
import { SettingsField } from "./_shared";
import ImageUploader from "./ImageUploader";

const EMPTY_FORM = {
  discord_url: "",
  buymecoffee_url: "",
  background_url: "",
  site_tagline: "",
  sponsor_url: "",
  sponsor_name: "",
  // Iter 39 — frontpage editor fields.
  hero_image_url: "",
  hero_eyebrow: "",
  hero_line1: "",
  hero_line2: "",
  hero_line3: "",
  hero_subtitle: "",
  hero_cta_primary_label: "",
  hero_cta_primary_url: "",
  hero_cta_secondary_label: "",
  hero_cta_secondary_url: "",
};

export default function SiteSettingsAdmin() {
  const { settings, save } = useSiteSettings();
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (settings) {
      const next = { ...EMPTY_FORM };
      Object.keys(EMPTY_FORM).forEach((k) => {
        next[k] = settings[k] || "";
      });
      setForm(next);
    }
  }, [settings]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await save(form);
      toast.success(t.saved);
    } catch (err) {
      toast.error(t.failed);
    } finally { setBusy(false); }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="site-settings-form"
      className="mt-8 max-w-2xl space-y-5 border border-white/10 bg-[#0C0C14] p-6"
    >
      <div>
        <h2 className="font-display uppercase italic text-2xl">{t.siteSettings}</h2>
        <p className="text-xs text-white/50 font-mono mt-1">{t.siteSettingsHint}</p>
      </div>
      <SettingsField
        label={t.discordUrl}
        value={form.discord_url}
        onChange={(v) => setForm({ ...form, discord_url: v })}
        placeholder="https://discord.gg/your-invite"
        testid="settings-discord"
      />
      <SettingsField
        label={t.buymeacoffeeUrl}
        value={form.buymecoffee_url}
        onChange={(v) => setForm({ ...form, buymecoffee_url: v })}
        placeholder="https://www.buymeacoffee.com/yourname"
        testid="settings-bmc"
      />
      <SettingsField
        label={t.backgroundImageUrl}
        value={form.background_url}
        onChange={(v) => setForm({ ...form, background_url: v })}
        placeholder="https://images.unsplash.com/photo-...?fm=jpg"
        testid="settings-bg"
      />
      <div className="-mt-3">
        <ImageUploader
          onChange={(url) => setForm((f) => ({ ...f, background_url: url }))}
          testid="settings-bg-upload"
          label={t.uploadBackground || "Upload background"}
        />
      </div>
      <SettingsField
        label={t.siteTagline}
        value={form.site_tagline}
        onChange={(v) => setForm({ ...form, site_tagline: v })}
        placeholder={t.tagline}
        testid="settings-tagline"
      />
      <SettingsField
        label={t.sponsorUrl}
        value={form.sponsor_url}
        onChange={(v) => setForm({ ...form, sponsor_url: v })}
        placeholder="https://www.twierdzapasji.pl/"
        testid="settings-sponsor-url"
      />
      <SettingsField
        label={t.sponsorName}
        value={form.sponsor_name}
        onChange={(v) => setForm({ ...form, sponsor_name: v })}
        placeholder="Twierdza Pasji"
        testid="settings-sponsor-name"
      />
      {form.background_url && (
        <div
          data-testid="settings-bg-preview"
          className="h-32 border border-white/10 bg-cover bg-center"
          style={{ backgroundImage: `url("${form.background_url}")` }}
        />
      )}

      {/* Iter 39 — Frontpage / Hero editor. Every field is optional; empty
          values fall back to the localized i18n defaults on the public Home. */}
      <div className="pt-6 border-t border-white/10 space-y-5">
        <div>
          <h3 className="font-display uppercase italic text-xl">{t.frontpageEditor}</h3>
          <p className="text-xs text-white/50 font-mono mt-1">{t.frontpageEditorHint}</p>
        </div>
        <SettingsField
          label={t.heroImageUrlLabel}
          value={form.hero_image_url}
          onChange={(v) => setForm({ ...form, hero_image_url: v })}
          placeholder="https://images.pexels.com/..."
          testid="settings-hero-image"
        />
        <div className="-mt-3">
          <ImageUploader
            onChange={(url) => setForm((f) => ({ ...f, hero_image_url: url }))}
            testid="settings-hero-upload"
            label={t.uploadHero || "Upload hero image"}
          />
        </div>
        {form.hero_image_url && (
          <div
            data-testid="settings-hero-image-preview"
            className="h-40 border border-white/10 bg-cover bg-center"
            style={{ backgroundImage: `url("${form.hero_image_url}")` }}
          />
        )}
        <SettingsField
          label={t.heroEyebrowLabel}
          value={form.hero_eyebrow}
          onChange={(v) => setForm({ ...form, hero_eyebrow: v })}
          placeholder={t.heroEyebrow}
          testid="settings-hero-eyebrow"
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SettingsField
            label={t.heroLine1Label}
            value={form.hero_line1}
            onChange={(v) => setForm({ ...form, hero_line1: v })}
            placeholder={t.heroLine1}
            testid="settings-hero-line1"
          />
          <SettingsField
            label={t.heroLine2Label}
            value={form.hero_line2}
            onChange={(v) => setForm({ ...form, hero_line2: v })}
            placeholder={t.heroLine2}
            testid="settings-hero-line2"
          />
          <SettingsField
            label={t.heroLine3Label}
            value={form.hero_line3}
            onChange={(v) => setForm({ ...form, hero_line3: v })}
            placeholder={t.heroLine3}
            testid="settings-hero-line3"
          />
        </div>
        <SettingsField
          label={t.heroSubtitleLabel}
          value={form.hero_subtitle}
          onChange={(v) => setForm({ ...form, hero_subtitle: v })}
          placeholder={t.heroSubtitle}
          testid="settings-hero-subtitle"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SettingsField
            label={t.heroCta1Label}
            value={form.hero_cta_primary_label}
            onChange={(v) => setForm({ ...form, hero_cta_primary_label: v })}
            placeholder={t.heroBtnTournaments}
            testid="settings-hero-cta1-label"
          />
          <SettingsField
            label={t.heroCta1Url}
            value={form.hero_cta_primary_url}
            onChange={(v) => setForm({ ...form, hero_cta_primary_url: v })}
            placeholder="/tournaments"
            testid="settings-hero-cta1-url"
          />
          <SettingsField
            label={t.heroCta2Label}
            value={form.hero_cta_secondary_label}
            onChange={(v) => setForm({ ...form, hero_cta_secondary_label: v })}
            placeholder={t.heroBtnDecks}
            testid="settings-hero-cta2-label"
          />
          <SettingsField
            label={t.heroCta2Url}
            value={form.hero_cta_secondary_url}
            onChange={(v) => setForm({ ...form, hero_cta_secondary_url: v })}
            placeholder="/decks"
            testid="settings-hero-cta2-url"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy}
          data-testid="settings-save"
          className="px-6 py-3 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs disabled:opacity-50"
        >
          {busy ? t.saving : t.save}
        </button>
      </div>
    </form>
  );
}
